import { useState } from 'react';
import { MAIL_STATUSES, MAIL_STATUS_LABELS } from '@faktorplus/shared';
import { useProjects } from '../../api/dictionaries';
import { Button } from '../../components/ui/button';
import { FilterIcon } from '../../components/ui/icons';
import { Field, Input, Select } from '../../components/ui/form';
import { Modal, modalStyles } from '../../components/ui/modal';
import { formatDate } from '../../lib/format';
import styles from '../invoices/invoice-filters.module.css';

/**
 * The orders history filter bar — the same shape as the register's: search out
 * in the open, everything else behind one "Filtry" button, and a chip per
 * active filter so a filtered list never looks unfiltered.
 */

const DIALOG_KEYS = ['status', 'projectId', 'placedFrom', 'placedTo'] as const;

type DialogKey = (typeof DIALOG_KEYS)[number];
type Draft = Record<DialogKey, string>;

interface OrderFiltersProps {
  params: URLSearchParams;
  onChange: (patch: Record<string, string>) => void;
  onReset: () => void;
}

export function OrderFilters({ params, onChange, onReset }: OrderFiltersProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { data: projects = [] } = useProjects(true);

  const activeCount = countActive(params);
  const chips = describeActive(params, projects);

  return (
    <>
      <div className={styles.bar}>
        <Input
          className={styles.search}
          placeholder="Szukaj po kontrahencie, e-mailu lub mieście…"
          aria-label="Szukaj"
          value={params.get('search') ?? ''}
          onChange={(event) => onChange({ search: event.target.value })}
        />

        <Button
          className={activeCount > 0 ? styles.ctaActive : undefined}
          onClick={() => setIsOpen(true)}
          aria-expanded={isOpen}
        >
          <FilterIcon className={styles.filterIcon} />
          Filtry
          {activeCount > 0 && <span className={styles.count}>{activeCount}</span>}
        </Button>
      </div>

      {chips.length > 0 && (
        <div className={styles.chips}>
          {chips.map((chip) => (
            <span key={chip.key} className={styles.chip}>
              <span className={styles.chipLabel}>{chip.label}</span>
              <span className={styles.chipValue}>{chip.value}</span>
              <button
                type="button"
                className={styles.chipRemove}
                aria-label={`Usuń filtr: ${chip.label} ${chip.value}`}
                onClick={() => onChange(chip.clear)}
              >
                ×
              </button>
            </span>
          ))}
          <button type="button" className={styles.clearAll} onClick={onReset}>
            Wyczyść wszystkie
          </button>
        </div>
      )}

      {isOpen && (
        <FilterDialog
          projects={projects.map((project) => ({ value: project.id, label: project.name }))}
          initial={readDraft(params)}
          onApply={(draft) => {
            onChange(draft);
            setIsOpen(false);
          }}
          onClose={() => setIsOpen(false)}
        />
      )}
    </>
  );
}

interface FilterDialogProps {
  projects: { value: string; label: string }[];
  initial: Draft;
  onApply: (draft: Draft) => void;
  onClose: () => void;
}

function FilterDialog({ projects, initial, onApply, onClose }: FilterDialogProps) {
  const [draft, setDraft] = useState<Draft>(initial);

  function set(key: DialogKey, value: string) {
    setDraft((current) => ({ ...current, [key]: value }));
  }

  return (
    <Modal
      title="Filtry zamówień"
      isOpen
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={() => setDraft(emptyDraft())}>
            Wyczyść
          </Button>
          <span className={modalStyles.footerSpacer} />
          <Button onClick={onClose}>Anuluj</Button>
          <Button variant="primary" onClick={() => onApply(draft)}>
            Zastosuj
          </Button>
        </>
      }
    >
      <Field label="Status wysyłki">
        {(id) => (
          <Select id={id} value={draft.status} onChange={(e) => set('status', e.target.value)}>
            <option value="">Wszystkie statusy</option>
            {MAIL_STATUSES.map((status) => (
              <option key={status} value={status}>
                {MAIL_STATUS_LABELS[status]}
              </option>
            ))}
          </Select>
        )}
      </Field>

      <Field label="Budowa">
        {(id) => (
          <Select id={id} value={draft.projectId} onChange={(e) => set('projectId', e.target.value)}>
            <option value="">Wszystkie budowy</option>
            {projects.map((project) => (
              <option key={project.value} value={project.value}>
                {project.label}
              </option>
            ))}
          </Select>
        )}
      </Field>

      <Field label="Data złożenia">
        {(id) => (
          <div className={styles.range}>
            <Input
              id={id}
              type="date"
              aria-label="Złożone od"
              max={draft.placedTo || undefined}
              value={draft.placedFrom}
              onChange={(event) => set('placedFrom', event.target.value)}
            />
            <span className={styles.rangeSeparator}>—</span>
            <Input
              type="date"
              aria-label="Złożone do"
              min={draft.placedFrom || undefined}
              value={draft.placedTo}
              onChange={(event) => set('placedTo', event.target.value)}
            />
          </div>
        )}
      </Field>
    </Modal>
  );
}

/** Whether anything is narrowing the list. */
export function hasAnyOrderFilter(params: URLSearchParams): boolean {
  return ['search', ...DIALOG_KEYS].some((key) => params.get(key));
}

interface Chip {
  key: string;
  label: string;
  value: string;
  clear: Record<string, string>;
}

function describeActive(params: URLSearchParams, projects: { id: string; name: string }[]): Chip[] {
  const chips: Chip[] = [];

  const status = params.get('status');
  if (status && MAIL_STATUSES.includes(status as (typeof MAIL_STATUSES)[number])) {
    chips.push({
      key: 'status',
      label: 'Status',
      value: MAIL_STATUS_LABELS[status as (typeof MAIL_STATUSES)[number]],
      clear: { status: '' },
    });
  }

  const projectId = params.get('projectId');
  if (projectId) {
    const project = projects.find((candidate) => candidate.id === projectId);
    chips.push({
      key: 'projectId',
      label: 'Budowa',
      value: project?.name ?? 'nieznana wartość',
      clear: { projectId: '' },
    });
  }

  const range = describePlacedRange(params);
  if (range) {
    chips.push({
      key: 'placed',
      label: 'Data złożenia',
      value: range,
      clear: { placedFrom: '', placedTo: '' },
    });
  }

  return chips;
}

function describePlacedRange(params: URLSearchParams): string | null {
  const from = params.get('placedFrom');
  const to = params.get('placedTo');

  if (from && to) return `${formatDate(from)} – ${formatDate(to)}`;
  if (from) return `od ${formatDate(from)}`;
  if (to) return `do ${formatDate(to)}`;
  return null;
}

/** The search box speaks for itself; a date range is one filter either way. */
function countActive(params: URLSearchParams): number {
  const singles = ['status', 'projectId'].filter((key) => params.get(key)).length;
  return singles + (params.get('placedFrom') || params.get('placedTo') ? 1 : 0);
}

function readDraft(params: URLSearchParams): Draft {
  return Object.fromEntries(DIALOG_KEYS.map((key) => [key, params.get(key) ?? ''])) as Draft;
}

function emptyDraft(): Draft {
  return Object.fromEntries(DIALOG_KEYS.map((key) => [key, ''])) as Draft;
}
